import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { Loading } from 'src/shared/components/loading/loading';
import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Me } from 'src/providers/me/me';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { PosthogService } from 'src/shared/services/posthog.service';

import { ClientSettingsModel } from 'src/models/client.model';

import validationUtils from 'src/shared/util/validation-utils';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { SuccessPage } from 'src/shared/pages/success/success';
import { parsePhoneNumber } from 'src/tidy-ui-components/components/form/input-international-phone/international-number.utils';

@Component({
    templateUrl: 'contact-info.html',
    standalone: false
})

export class ContactInfoPage implements OnInit {

  form: UntypedFormGroup;
  settings: ClientSettingsModel;
  submitted: boolean;
  showCompanyName: boolean;
  errorMessage: string;
  dialogParams: any;
  isRightSideContent = true;
  loaded: boolean;
  countryCode: string;

  
  boundSave = this.save.bind(this);

constructor(
    private client: Client,
    private fb: UntypedFormBuilder,
    private me: Me,
    private storage: TidyStorage,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private posthogService: PosthogService,
  ) {
    this.form = this.fb.group({
      companyName: [''],
      email: ['', [Validators.required, validationUtils.validateEmail]],
      billEmail: ['', [Validators.required, validationUtils.validateEmail]],
      firstName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      lastName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      phone: ['', Validators.required],
    });
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.rightSidePanelService.setDialogPageTitle('Contact Info');
    this.loadData();
  }

  @Loading('', true)
  async loadData() {
    try {
      this.rightSidePanelService.setDialogLoading(true);
      this.loaded = false;
      this.settings = await this.client.getClientSettings();
      const accountType = localStorage.getItem('accountType');
      const userRole = localStorage.getItem('user_role');
      this.showCompanyName = (accountType === 'rental' || accountType === 'company') && userRole !== 'member';
      this.form.patchValue({
        firstName: this.settings.profile.first_name,
        lastName: this.settings.profile.last_name,
        email: this.settings.profile.email,
        phone: this.settings.profile.phone !== null ? this.settings.profile.phone : '',
        companyName: this.settings.profile.account_name,
        billEmail: this.settings.invoices.alt_email !== '' && this.settings.invoices.alt_email ? this.settings.invoices.alt_email : this.settings.profile.email
      });
      localStorage.setItem('clientEmail', this.settings.profile.email || '');
      this.errorMessage = '';
      this.loaded = true;
      this.rightSidePanelService.setDialogLoading(false);
    } catch (_err) {
      // Silent error handling
    }
  }

  @Loading('', true)
  async save() {
    this.submitted = true;
    if (!this.form.valid) {
      return;
    }
    if (this.form.value.firstName.trim().length < 2 || this.form.value.lastName.trim().length < 2) {
      this.form.patchValue({
        firstName: this.form.value.firstName.trim(),
        lastName: this.form.value.lastName.trim()
      });
      return;
    }
    const payload: any = {
      profile: {
        last_name: this.form.value.lastName.trim(),
        first_name: this.form.value.firstName.trim(),
        phone: parsePhoneNumber(this.form.value.phone),
        email: this.form.value.email,
        account_name: this.form.value.companyName,
        alt_email: this.form.value.billEmail === '' ? null : this.form.value.billEmail,
      }
    }
    try {
      await this.client.saveClientSettings(payload);
      localStorage.setItem('didMakeMoreChange', 'true');
      localStorage.setItem('clientEmail', this.form.value.email || '');
      await this.storage.save('phoneNumber', this.form.value.phone);
      await this.me.load(true);
      await this.posthogService.setUserProperties({ email: this.form.value.email });
      const params = this.mountSuccessPageParams();
      const url = 'success';
      const component = SuccessPage;
      this.rightSidePanelService.navigateTo(url, params, component);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  mountSuccessPageParams() {
    return {
      header: 'Contact Info Saved',
      body: '',
      buttonText: 'Ok',
      buttonRoute: 'more'
    };
  }

  onCountryCodeChange(countryCode: string) {
    this.countryCode = countryCode.replace('+', '');
  }
}
